import { api } from '@/lib/api';
import { API_ENDPOINTS } from '@/utils/constants';

export interface CounselorPresenceStatus {
  counselorId: string;
  isLive: boolean;
  availabilityStatus: 'online' | 'busy' | 'offline';
  lastSeenAt?: string;
  connectionCount: number;
  currentActivity?: string;
}

export interface PresenceUpdateRequest {
  availabilityStatus: 'online' | 'busy' | 'offline';
  currentActivity?: string;
}

export interface PresenceActivity {
  counselorId: string;
  activity: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface BulkPresenceResponse {
  counselors: CounselorPresenceStatus[];
  onlineCount: number;
  timestamp: string;
}

/**
 * Get current counselor's presence status
 */
export const getCurrentCounselorPresence = async (
  counselorId: string
): Promise<CounselorPresenceStatus> => {
  return api.get<CounselorPresenceStatus>(
    API_ENDPOINTS.PRESENCE.COUNSELOR.STATUS(counselorId)
  );
};

/**
 * Update current counselor's presence status
 */
export const updateCounselorPresence = async (
  _counselorId: string,
  update: PresenceUpdateRequest
): Promise<CounselorPresenceStatus> => {
  // Server expects POST /presence/my-status with body { status, reason? }
  const payload: any = { status: update.availabilityStatus };
  if (update.currentActivity) payload.reason = update.currentActivity;
  return api.post<CounselorPresenceStatus>(
    '/presence/my-status',
    payload
  );
};

/**
 * Get presence status for multiple counselors (admin use)
 */
export const getBulkCounselorPresence = async (
  counselorIds: string[]
): Promise<BulkPresenceResponse> => {
  return api.post<BulkPresenceResponse>(
    API_ENDPOINTS.PRESENCE.COUNSELOR.BULK_STATUS,
    { counselorIds }
  );
};

/**
 * Get online counselors count
 */
export const getOnlineCounselorsCount = async (): Promise<{ count: number; timestamp: string }> => {
  return api.get<{ count: number; timestamp: string }>(
    API_ENDPOINTS.PRESENCE.COUNSELOR.ONLINE_COUNT
  );
};

/**
 * Get counselor activity history
 */
export const getCounselorActivity = async (
  counselorId: string,
  params?: {
    limit?: number;
    startDate?: string;
    endDate?: string;
  }
): Promise<PresenceActivity[]> => {
  return api.get<PresenceActivity[]>(
    API_ENDPOINTS.PRESENCE.COUNSELOR.ACTIVITY(counselorId),
    params
  );
};

/**
 * Set counselor status to online
 */
export const goOnline = async (counselorId: string): Promise<CounselorPresenceStatus> => {
  return updateCounselorPresence(counselorId, {
    availabilityStatus: 'online',
  });
};

/**
 * Set counselor status to busy
 */
export const goBusy = async (
  counselorId: string,
  activity?: string
): Promise<CounselorPresenceStatus> => {
  return updateCounselorPresence(counselorId, {
    availabilityStatus: 'busy',
    currentActivity: activity,
  });
};

/**
 * Set counselor status to offline
 */
export const goOffline = async (counselorId: string): Promise<CounselorPresenceStatus> => {
  return updateCounselorPresence(counselorId, {
    availabilityStatus: 'offline',
  });
};

/**
 * Check if counselor can accept new sessions
 */
export const canAcceptSessions = (status: CounselorPresenceStatus): boolean => {
  return status.isLive && status.availabilityStatus === 'online';
};

/**
 * Format last seen time for display
 */
export const formatLastSeen = (lastSeenAt?: string): string => {
  if (!lastSeenAt) return 'Never';
  
  const lastSeen = new Date(lastSeenAt);
  const now = new Date();
  const diffMs = now.getTime() - lastSeen.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return lastSeen.toLocaleDateString();
};

/**
 * Get status display configuration for UI components
 */
export const getStatusDisplayConfig = (status: CounselorPresenceStatus) => {
  if (!status.isLive) {
    return {
      text: 'Offline',
      color: 'gray',
      dotColor: 'bg-gray-500',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
      canAcceptSessions: false,
      showPulse: false,
    };
  }

  switch (status.availabilityStatus) {
    case 'online':
      return {
        text: 'Available',
        color: 'green',
        dotColor: 'bg-green-500',
        bgColor: 'bg-green-100',
        textColor: 'text-green-600',
        canAcceptSessions: true,
        showPulse: true,
      };
    case 'busy':
      return {
        text: status.currentActivity || 'Busy',
        color: 'yellow',
        dotColor: 'bg-yellow-500',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-600',
        canAcceptSessions: false,
        showPulse: false,
      };
    default:
      return {
        text: 'Offline',
        color: 'gray',
        dotColor: 'bg-gray-500',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-600',
        canAcceptSessions: false,
        showPulse: false,
      };
  }
};

/**
 * Get available status options for dropdowns
 */
export const getAvailableStatusOptions = () => [
  { value: 'online', label: 'Available', color: 'green' },
  { value: 'busy', label: 'Busy', color: 'yellow' },
  { value: 'offline', label: 'Offline', color: 'gray' },
];